import {
	FOOD_BOUGHT_LISTS_LOADED,
	FOOD_CHANGES_RECEIVED,
	FOOD_CLEARED,
	FOOD_OP_APPLIED,
	FOOD_OP_ENQUEUED,
	FOOD_OP_REJECTED,
	FOOD_OPS_RESTORED,
	FOOD_RANGE_BEGIN,
	FOOD_RANGE_FAIL,
	FOOD_RANGE_LOADED,
	FOOD_REFRESH_DIRTY,
	FOOD_REJECTED_DISMISSED,
	FOOD_RESTORED,
	FOOD_RESULTS_FOLDED,
	FOOD_RESULTS_PRUNED,
	FOOD_SNAPSHOT_BEGIN,
	FOOD_SNAPSHOT_FAIL,
	FOOD_SNAPSHOT_SUCCESS,
	FOOD_SYNC_STATE,
} from '../actions/food';
import { applyBoughtLists, applyChanges, applyRange, applySnapshot, emptyEntities } from '../food/normalize';
import { legacyChanges } from '../food/legacyChanges';
import { applyOp, rebase } from '../food/applyOp';

/**
 * One budget's food data (docs/local-first-food-data.md, phases 2 and 3): what the server last said
 * (`confirmed`), the ops not yet answered (`pending`, in the order they were made), and what screens
 * read (`entities`): `confirmed` with every pending op applied over it (food/applyOp.js), the same
 * object while none is pending. Plain JSON only, so the same object is persisted and rendered.
 *
 * An op's result is folded once, by client_id (`folded`), whether it came from this tab's sender,
 * another tab's over the channel, or the op_results store on load. Applied, its changes are merged
 * into `confirmed` and kept in `recent` like any other answer. A replay brings no changes (the server
 * applied the op earlier, and that answer was lost), so the op itself is applied to `confirmed` and
 * kept in `recent` in their place: the change stays on screen until a snapshot asked for after it,
 * which the result's thunk asks for, says what the server has. Rejected, it leaves `pending`, so its
 * effect disappears, and is kept in `rejected` until dismissed.
 *
 * Every action naming a budget other than the one held is ignored, as is a legacy write's answer
 * echoing another budget, or none.
 *
 * Snapshots and write answers are ordered by `epoch`, a local counter: a snapshot request takes the
 * next one, and so does every answer merged into `confirmed`, which is also kept in `recent`. When a
 * snapshot arrives, the answers merged after it was requested are applied again over it, since the
 * server may have read the snapshot before those writes committed. Earlier ones are already in it.
 * A food/range request takes an epoch too, and the answers merged after it are applied again over
 * the months it brings. A snapshot drops the range requests made before it, since the answers merged
 * between the two have left `recent`; the refresh asks for those months again.
 */

/** Written out rather than imported from actions/sharing, which is not loaded while the store is built. */
const CHANGE_ACTIVE_BUDGET_SUCCESS = 'CHANGE_ACTIVE_BUDGET_SUCCESS';

const freshState = () => {
	const confirmed = emptyEntities();
	return {
		loadId: 0,
		userId: null,
		budgetId: null,
		status: 'empty',
		persistent: true,
		refreshing: false,
		refreshDirty: false,
		error: null,
		// The browser's clock, in seconds, when the last snapshot arrived.
		lastSyncedAt: null,
		epoch: 0,
		snapshotEpoch: 0,
		// [generated_at of the snapshot it is built on, answers merged since]; orders saves across tabs.
		version: [0, 0],
		coverage: null,
		// food/range requests in flight, `{ epoch, from, to }`.
		rangesInFlight: [],
		confirmed,
		recent: [],
		pending: [],
		entities: confirmed,
		rejected: [],
		// The op queue's state for this budget; `unsent` and `anySending` are of every budget's (foodSyncState).
		sync: { sending: false, lastError: null, nextRetryAt: null, paused: null, persistent: true, unsent: null, anySending: false },
		// client_id => { epoch, status } of each op result folded, so it is folded once.
		folded: {},
		// The epoch of the last snapshot applied: results folded before it are in it.
		appliedSnapshotEpoch: 0,
	};
};

const initialState = freshState();

const forBudget = (state, payload) => !!payload && state.budgetId !== null && Number(payload.budgetId) === state.budgetId;

/** `state` with `confirmed` (and `extra`), and what screens read worked out again. */
const withConfirmed = (state, confirmed, extra = {}) => {
	const next = { ...state, ...extra, confirmed };
	next.entities = rebase(confirmed, next.pending);
	return next;
};

const withPending = (state, pending, extra = {}) => ({
	...state,
	...extra,
	pending,
	entities: rebase(state.confirmed, pending),
});

const bySeq = (a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0);

const hasChanges = (changes) => !!changes && ['upsert', 'delete'].some((part) => changes[part] && Object.keys(changes[part]).length);

/**
 * An op's result folded in, once. `record` is what the op queue stores: `{ client_id, result, op }`.
 */
const foldResult = (state, record) => {
	const id = record && record.client_id;
	const result = (record && record.result) || {};
	if(!id || state.folded[id] || (result.status !== 'applied' && result.status !== 'rejected')) {
		return state;
	}
	const pending = state.pending.filter((op) => op.client_id !== id);
	const op = state.pending.find((each) => each.client_id === id) || { client_id: id, ...(record.op || {}) };
	if(result.status === 'rejected') {
		const entry = {
			client_id: id,
			type: op.type,
			v: op.v,
			payload: op.payload,
			error: result.error || 'The server refused this change.',
			at: record.at || null,
		};
		return withPending(state, pending, {
			folded: { ...state.folded, [id]: { epoch: state.epoch, status: 'rejected' } },
			rejected: [...state.rejected.filter((each) => each.client_id !== id), entry],
		});
	}
	if(result.replayed) {
		const epoch = state.epoch + 1;
		return withConfirmed(state, applyOp(state.confirmed, op), {
			pending,
			epoch,
			folded: { ...state.folded, [id]: { epoch, status: 'applied' } },
			recent: [...state.recent, { epoch, op }],
			version: [state.version[0], state.version[1] + 1],
		});
	}
	if(!hasChanges(result.changes)) {
		return withPending(state, pending, { folded: { ...state.folded, [id]: { epoch: state.epoch, status: 'applied' } } });
	}
	const epoch = state.epoch + 1;
	return withConfirmed(state, applyChanges(state.confirmed, result.changes), {
		pending,
		epoch,
		folded: { ...state.folded, [id]: { epoch, status: 'applied' } },
		recent: [...state.recent, { epoch, op, changes: result.changes }],
		version: [state.version[0], state.version[1] + 1],
	});
};

/** Whether two lists of ops are the same ops, in the same order, at the same seq. */
const sameOps = (a, b) => a.length === b.length && a.every((op, index) => op.client_id === b[index].client_id && op.seq === b[index].seq);

/** `confirmed` with the answers in `recent` merged after `epoch` applied again: a replay's by its op, which brought no changes. */
const reapplyAfter = (confirmed, recent, epoch) => recent
	.filter((entry) => entry.epoch > epoch)
	.reduce((entities, entry) => (entry.changes ? applyChanges(entities, entry.changes) : applyOp(entities, entry.op)), confirmed);

/** An answer merged in: kept in `recent` under the next epoch, for the next snapshot to apply again. */
const mergeChanges = (state, changes) => {
	const epoch = state.epoch + 1;
	return withConfirmed(state, applyChanges(state.confirmed, changes), {
		epoch,
		recent: [...state.recent, { epoch, changes }],
		version: [state.version[0], state.version[1] + 1],
	});
};

/** Ranges loaded since a snapshot: one wholly inside the new range is replaced by it. */
const withRange = (coverage, from, to) => {
	const base = coverage || { from: null, to: null, ranges: [], olderBoughtLists: true };
	return {
		...base,
		ranges: [...base.ranges.filter((range) => range.from < from || range.to > to), { from, to, stale: false }],
	};
};

export default function foodReducer(state = initialState, action) {
	if(action.meta && action.meta.fromFood) {
		return state;
	}
	const payload = action.payload;
	switch(action.type) {
		case FOOD_CLEARED: {
			const cleared = { ...freshState(), loadId: state.loadId + 1 };
			if(!payload) {
				return cleared;
			}
			return {
				...cleared,
				userId: Number(payload.userId),
				budgetId: Number(payload.budgetId),
				persistent: payload.persistent !== false,
			};
		}
		// Another budget, at once: an answer for the old one still in flight is dropped from here on.
		// loadFood() then restores and refreshes the new one.
		case CHANGE_ACTIVE_BUDGET_SUCCESS: {
			const activeBudget = payload && payload.active_budget;
			if(state.budgetId === null || !activeBudget || !activeBudget.id || Number(activeBudget.id) === state.budgetId) {
				return state;
			}
			return {
				...freshState(),
				loadId: state.loadId + 1,
				userId: state.userId,
				budgetId: Number(activeBudget.id),
				persistent: state.persistent,
			};
		}
		case FOOD_RESTORED: {
			// Only into an empty store: a snapshot that landed while the copy was being read is newer.
			if(!forBudget(state, payload) || Number(payload.userId) !== state.userId || state.status !== 'empty') {
				return state;
			}
			const { record } = payload;
			// The results the copy already holds are folded; any folded before it was read are folded again over it.
			const folded = {};
			(record.folded || []).forEach((id) => {
				folded[id] = { epoch: 0, status: 'applied' };
			});
			return withConfirmed(state, { ...emptyEntities(), ...record.confirmed }, {
				status: 'restored',
				coverage: record.coverage || null,
				version: record.version || [0, 0],
				lastSyncedAt: record.syncedAt || null,
				folded,
			});
		}
		case FOOD_SNAPSHOT_BEGIN: {
			if(!forBudget(state, payload)) {
				return state;
			}
			const epoch = state.epoch + 1;
			return { ...state, epoch, snapshotEpoch: epoch, refreshing: true, refreshDirty: false };
		}
		case FOOD_REFRESH_DIRTY:
			return forBudget(state, payload) ? { ...state, refreshDirty: true } : state;
		case FOOD_SNAPSHOT_SUCCESS: {
			if(!forBudget(state, payload)) {
				return state;
			}
			const { snapshot, receivedAt, epoch } = payload;
			const later = state.recent.filter((entry) => entry.epoch > epoch);
			const confirmed = reapplyAfter(applySnapshot(state.confirmed, snapshot), later, epoch);
			return withConfirmed(state, confirmed, {
				rangesInFlight: state.rangesInFlight.filter((range) => range.epoch > epoch),
				status: 'fresh',
				refreshing: false,
				error: null,
				lastSyncedAt: receivedAt,
				recent: later,
				version: [Number(snapshot.generated_at) || 0, later.length],
				appliedSnapshotEpoch: epoch,
				coverage: {
					from: snapshot.window.from,
					to: snapshot.window.to,
					// Kept, and shown, but fetched again the next time they are on screen.
					ranges: ((state.coverage && state.coverage.ranges) || []).map((range) => ({ ...range, stale: true })),
					olderBoughtLists: !!snapshot.has_older_bought_lists,
				},
			});
		}
		case FOOD_SNAPSHOT_FAIL: {
			if(!forBudget(state, payload)) {
				return state;
			}
			return {
				...state,
				refreshing: false,
				error: payload.error || null,
				status: state.status === 'empty' ? 'error' : state.status,
			};
		}
		case FOOD_RANGE_BEGIN: {
			if(!forBudget(state, payload)) {
				return state;
			}
			const epoch = state.epoch + 1;
			return { ...state, epoch, rangesInFlight: [...state.rangesInFlight, { epoch, from: payload.from, to: payload.to }] };
		}
		case FOOD_RANGE_FAIL: {
			if(!forBudget(state, payload)) {
				return state;
			}
			return { ...state, rangesInFlight: state.rangesInFlight.filter((range) => range.epoch !== payload.epoch) };
		}
		case FOOD_RANGE_LOADED: {
			// Only a request still in flight: one a later snapshot dropped is asked for again.
			if(!forBudget(state, payload) || !state.rangesInFlight.some((range) => range.epoch === payload.epoch)) {
				return state;
			}
			const { from, to, epoch } = payload;
			return withConfirmed(state, reapplyAfter(applyRange(state.confirmed, from, to, payload.payload), state.recent, epoch), {
				rangesInFlight: state.rangesInFlight.filter((range) => range.epoch !== epoch),
				coverage: withRange(state.coverage, from, to),
				version: [state.version[0], state.version[1] + 1],
			});
		}
		case FOOD_BOUGHT_LISTS_LOADED: {
			if(!forBudget(state, payload)) {
				return state;
			}
			// A search's page says nothing about the history as a whole.
			const coverage = state.coverage || { from: null, to: null, ranges: [] };
			return withConfirmed(state, applyBoughtLists(state.confirmed, payload.payload), {
				coverage: payload.query ? state.coverage : { ...coverage, olderBoughtLists: !!payload.payload.has_more },
				version: [state.version[0], state.version[1] + 1],
			});
		}
		case FOOD_CHANGES_RECEIVED:
			return forBudget(state, payload) ? mergeChanges(state, payload.changes) : state;
		// An op made here (twice: at once, then with its seq once written) or in another tab. Upserted by client_id.
		case FOOD_OP_ENQUEUED: {
			const op = payload && payload.op;
			if(!forBudget(state, payload) || !op || Number(op.budget_id) !== state.budgetId || state.folded[op.client_id]) {
				return state;
			}
			const index = state.pending.findIndex((each) => each.client_id === op.client_id);
			if(index === -1) {
				return withPending(state, [...state.pending, op]);
			}
			const pending = [...state.pending];
			pending[index] = op;
			return { ...state, pending };
		}
		// The ops the browser holds for the budget. One made here and not yet written is kept.
		case FOOD_OPS_RESTORED: {
			if(!forBudget(state, payload)) {
				return state;
			}
			const stored = (payload.ops || [])
				.filter((op) => Number(op.budget_id) === state.budgetId && !state.folded[op.client_id])
				.sort(bySeq);
			const ids = new Set(stored.map((op) => op.client_id));
			const pending = [...stored, ...state.pending.filter((op) => (op.seq === null || op.seq === undefined) && !ids.has(op.client_id))];
			return sameOps(pending, state.pending) ? state : withPending(state, pending);
		}
		case FOOD_OP_APPLIED:
		case FOOD_OP_REJECTED:
			return forBudget(state, payload) ? foldResult(state, payload.result) : state;
		case FOOD_RESULTS_FOLDED:
			return forBudget(state, payload) ? (payload.results || []).reduce(foldResult, state) : state;
		case FOOD_RESULTS_PRUNED: {
			if(!forBudget(state, payload)) {
				return state;
			}
			const folded = { ...state.folded };
			(payload.clientIds || []).forEach((id) => {
				delete folded[id];
			});
			return { ...state, folded };
		}
		case FOOD_REJECTED_DISMISSED: {
			if(!forBudget(state, payload)) {
				return state;
			}
			const dismissed = new Set(payload.clientIds || []);
			return { ...state, rejected: state.rejected.filter((entry) => !dismissed.has(entry.client_id)) };
		}
		case FOOD_SYNC_STATE:
			return forBudget(state, payload) ? { ...state, sync: { ...state.sync, ...payload.sync } } : state;
		default: {
			if(state.budgetId === null) {
				return state;
			}
			let legacy = null;
			try {
				legacy = legacyChanges(action, state.confirmed);
			} catch(error) {
				// A legacy answer the bridge cannot read must not break the screen showing it.
				console.error(error);
			}
			if(!legacy || legacy.budgetId !== state.budgetId) {
				return state;
			}
			return mergeChanges(state, legacy.changes);
		}
	}
}
